import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import FormattedTimeStat from 'src/features/pricingPlan/components/FormattedTimeStat';
import { useGetPricingPlan } from 'src/features/pricingPlan/api/useGetPricingPlan';
import { useListPricingPlanTiers } from 'src/features/pricingPlan/api/tiers/useListPricingPlanTiers';
import CreateTierFormModal from 'src/features/pricingPlan/components/form/tiers/CreateTierFormModal';
import CellTierActions from 'src/features/pricingPlan/components/table/CellTierActions';
import { tickTypeBadgeOptions } from 'src/features/pricingPlan/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PricingPlanTier } from 'src/features/pricingPlan/utils/types';
import { CellBadge, DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import usePagination from 'src/registry/hooks/usePagination';
import useSorting from 'src/registry/hooks/useSorting';
import { commonMessages } from 'src/utils/messages';
import { CURRENCY } from 'src/types';
import { useParking } from 'src/providers/ParkingProvider';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';

const PricingPlanTiersTable = ({ currency }: { currency?: CURRENCY }) => {
    const { formatMessage, formatNumber } = useIntl();
    const { planId = '' } = useParams();
    const { parkingId } = useParking();

    const managePricingPlanTiers = useHasPermissions([PERMISSION.MANAGE_PRICING_PLAN_TIERS]);

    // Default (standard) plans are read-only — hide the "new entry" button.
    const { data: pricingPlan } = useGetPricingPlan({
        planId,
        parkingId,
        config: { enabled: !!planId && !!parkingId },
    });
    const isSystem = !!pricingPlan?.is_system;

    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'order', desc: false }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const {
        data: pricingPlanTiersData,
        isLoading,
        isError,
    } = useListPricingPlanTiers({
        planId,
        parkingId,
        ...querySortingOptions,
        ...queryPaginationOptions,
        config: {
            enabled: !!planId && !!parkingId,
        },
    });

    const columnHelper = createColumnHelper<PricingPlanTier>();

    const columns = [
        columnHelper.accessor('order', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: '#',
            },
            maxSize: 45,
        }),
        columnHelper.accessor('start_minute', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(pricingPlanMessages.startMinute),
            },
        }),
        columnHelper.accessor('end_minute', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(pricingPlanMessages.endMinute),
            },
        }),
        columnHelper.accessor('tick_type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(commonMessages.type),
                badgeOptions: tickTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('tick_minutes', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: ({ row }) => {
                return (
                    <div className="flex flex-col items-start">
                        <div>{row.original.tick_minutes}</div>
                        <div className="whitespace-nowrap">
                            (<FormattedTimeStat totalMinutes={row.original.tick_minutes} />)
                        </div>
                    </div>
                );
            },
            meta: {
                title: formatMessage(pricingPlanMessages.tickDurationMinutes),
            },
        }),
        columnHelper.accessor('rate_with_tax', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: ({ row }) => {
                const value = row.original.rate_with_tax;
                return value
                    ? formatNumber(value / 100, {
                          style: 'currency',
                          currency: currency,
                      })
                    : '-';
            },
            meta: {
                title: formatMessage(commonMessages.rate),
            },
        }),
        ...(managePricingPlanTiers
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellTierActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<PricingPlanTier, any>
            isLoading={isLoading}
            isError={isError}
            columns={columns}
            data={pricingPlanTiersData}
            sorting={sorting}
            pagination={pagination}
            enableSearchParams={false}
            semantics={{
                title: formatMessage(pricingPlanMessages.tiers),
                entryName: formatMessage(pricingPlanMessages.tier).toLowerCase(),
                entryNames: formatMessage(pricingPlanMessages.tiers).toLowerCase(),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(managePricingPlanTiers && !isSystem && { newEntry: CreateTierFormModal }),
            }}
        />
    );
};

export default PricingPlanTiersTable;
